"use client"

import { useMemo, useState } from "react"
import { Download, Printer, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { assignmentsForContact } from "@/lib/breach/assignments"
import { AssignForm } from "./assign-form"
import type { GoTo } from "./incident-nav"
import {
  addLog, uid, type Workspace,
} from "@/lib/breach/workspace"
import { toJson, toMarkdownSummary } from "@/lib/breach/export"

export type WorkspaceUpdater = (fn: (w: Workspace) => Workspace) => void

function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const a = document.createElement("a"); a.href = url; a.download = name; a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function Timeline({ w, update }: { w: Workspace; update: WorkspaceUpdater }) {
  const [text, setText] = useState("")
  const sorted = [...w.log].sort((a, b) => b.at.localeCompare(a.at))
  return (
    <section aria-labelledby="tl">
      <h1 id="tl" className="text-xl font-semibold sm:text-2xl">Timeline</h1>
      <p className="mt-1 text-sm text-muted-foreground">Write down what happens as it happens, with times. Regulators, insurers, and your lawyer will ask. Don&apos;t paste passwords, keys, or other people&apos;s personal data here.</p>
      <form className="mt-4 flex gap-2 print:hidden" onSubmit={(e) => { e.preventDefault(); update((x) => addLog(x, text)); setText("") }}>
        <input className="flex-1 rounded-md border border-border bg-background p-3" placeholder="e.g., Isolated the file server from the network" value={text} onChange={(e) => setText(e.target.value)} aria-label="New timeline entry" />
        <Button type="submit" size="lg">Add</Button>
      </form>
      <ol className="mt-6 grid gap-2">
        {sorted.map((e) => (
          <li key={e.id} className="rounded-md border border-border p-3 text-sm">
            <time className="mr-3 text-muted-foreground">{new Date(e.at).toLocaleString()}</time>{e.text}
          </li>
        ))}
      </ol>
    </section>
  )
}

export function Evidence({ w, update }: { w: Workspace; update: WorkspaceUpdater }) {
  const [f, setF] = useState({ what: "", from: "", collectedBy: "", location: "" })
  return (
    <section aria-labelledby="ev">
      <h1 id="ev" className="text-xl font-semibold sm:text-2xl">Evidence log</h1>
      <p className="mt-1 text-sm text-muted-foreground">A simple record of what you saved, from where, who collected it, and where it is now. This isn&apos;t forensic software, just a log to support chain of custody.</p>
      <form className="mt-4 grid gap-2 sm:grid-cols-2 print:hidden" onSubmit={(e) => {
        e.preventDefault(); if (!f.what.trim()) return
        update((x) => ({ ...x, updatedAt: new Date().toISOString(), evidence: [...x.evidence, { id: uid(), ...f, collectedAt: new Date().toISOString() }] }))
        setF({ what: "", from: "", collectedBy: "", location: "" })
      }}>
        {([["what", "What (e.g., firewall logs, disk image)"], ["from", "From (system / person)"], ["collectedBy", "Collected by"], ["location", "Stored at"]] as const).map(([k, ph]) => (
          <input key={k} className="rounded-md border border-border bg-background p-3" placeholder={ph} aria-label={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
        ))}
        <Button type="submit" size="lg" className="sm:col-span-2">Add evidence</Button>
      </form>
      <ul className="mt-6 grid gap-2">
        {w.evidence.map((e) => (
          <li key={e.id} className="flex items-start justify-between gap-3 rounded-md border border-border p-3 text-sm">
            <span><strong>{e.what}</strong> — from {e.from || "—"}; collected by {e.collectedBy || "—"} at {new Date(e.collectedAt).toLocaleString()}; stored: {e.location || "—"}</span>
            <button aria-label="Remove" className="text-muted-foreground hover:text-destructive print:hidden" onClick={() => update((x) => ({ ...x, evidence: x.evidence.filter((y) => y.id !== e.id) }))}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </section>
  )
}

const SUGGESTED_ROLES = ["Cyber insurer hotline", "Outside counsel", "Incident-response firm", "Your bank / payment processor", "FBI field office (or ic3.gov)", "Your IT / MSP", "Executive sponsor"]

export function Contacts({ w, update, go }: { w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const [assigning, setAssigning] = useState<string | null>(null)
  const [f, setF] = useState({ role: "", name: "", phone: "", notes: "" })
  return (
    <section aria-labelledby="ct">
      <h1 id="ct" className="text-xl font-semibold sm:text-2xl">Contacts</h1>
      <p className="mt-1 text-sm text-muted-foreground">Who to call, and their numbers, in one place. Print this page in case your network is down.</p>
      <form className="mt-4 grid gap-2 sm:grid-cols-2 print:hidden" onSubmit={(e) => {
        e.preventDefault(); if (!f.role.trim() && !f.name.trim()) return
        update((x) => ({ ...x, updatedAt: new Date().toISOString(), contacts: [...x.contacts, { id: uid(), ...f }] }))
        setF({ role: "", name: "", phone: "", notes: "" })
      }}>
        <input list="roles" className="rounded-md border border-border bg-background p-3" placeholder="Role" aria-label="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
        <datalist id="roles">{SUGGESTED_ROLES.map((r) => <option key={r} value={r} />)}</datalist>
        <input className="rounded-md border border-border bg-background p-3" placeholder="Name" aria-label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input className="rounded-md border border-border bg-background p-3" placeholder="Phone" aria-label="Phone" inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <input className="rounded-md border border-border bg-background p-3" placeholder="Notes (policy #, case #)" aria-label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <Button type="submit" size="lg" className="sm:col-span-2">Add contact</Button>
      </form>
      <ul className="mt-6 grid gap-2">
        {w.contacts.map((c) => (
          <li key={c.id} className="flex items-start justify-between gap-3 rounded-md border border-border p-3 text-sm">
            <div className="grid flex-1 gap-1">
              <span><strong>{c.role}</strong> — {c.name} {c.phone && <a className="underline" href={`tel:${c.phone}`}>{c.phone}</a>} {c.notes && <em className="text-muted-foreground">({c.notes})</em>}</span>
              {(() => {
                const mine = assignmentsForContact(w, c.id)
                const open = mine.filter((a) => a.status === "open").length
                return (
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground print:hidden">
                    {mine.length > 0 && <button type="button" className="underline" onClick={() => go({ v: "assignments" })}>{open} open of {mine.length} assigned</button>}
                    <button type="button" className="underline" onClick={() => setAssigning(assigning === c.id ? null : c.id)}>Assign a task</button>
                  </span>
                )
              })()}
              {assigning === c.id && <AssignForm w={w} update={update} defaults={{ contactId: c.id }} onClose={() => setAssigning(null)} />}
            </div>
            <button aria-label="Remove" className="text-muted-foreground hover:text-destructive print:hidden" onClick={() => update((x) => ({ ...x, contacts: x.contacts.filter((y) => y.id !== c.id) }))}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function Summary({ w, onDelete }: { w: Workspace; onDelete: () => void }) {
  const md = useMemo(() => toMarkdownSummary(w), [w])
  return (
    <section aria-labelledby="sm">
      <h1 id="sm" className="text-xl font-semibold sm:text-2xl">Summary &amp; export</h1>
      <p className="mt-1 text-sm text-muted-foreground">A hand-off you can give your team, your lawyer, or your insurer. Your data never leaves this browser unless you share the file.</p>
      <div className="mt-4 flex flex-wrap gap-2 print:hidden">
        <Button onClick={() => download(`incident-summary-${w.id.slice(0, 8)}.md`, md, "text/markdown")}><Download /> Summary (.md)</Button>
        <Button variant="outline" onClick={() => download(`incident-${w.id.slice(0, 8)}.json`, toJson(w), "application/json")}><Download /> Full backup (.json)</Button>
        <Button variant="outline" onClick={() => window.print()}><Printer /> Print</Button>
        <Button variant="outline" className="text-destructive" onClick={onDelete}><Trash2 /> Delete from this browser</Button>
      </div>
      <pre className="mt-6 max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-card p-4 text-xs">{md}</pre>
    </section>
  )
}
